'use client';
import {
  AssociatedDealerForm,
  DrawerBackHeader,
  Conditional
} from 'components/pcc-components';
import {
  CUSTOMER_NUMBER_RADIO_NAME,
  SEARCH_RESULTS_FORM_ID,
  DEFAULT_SEARCH_RESULTS_QUANTITY,
  STATUS_FAIL,
  STATUS_SUCCESS,
  ERRORS
} from '../../constants/constants';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import { useDealerSearch } from 'components/associated-dealers/hooks/use-dealer-search';
import { SearchResults } from '../search-results/search-results';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import {
  DealerSearchResultsFormData,
  FormType
} from 'components/associated-dealers/types';
import { useDealerCustomer } from 'components/associated-dealers/hooks/use-dealer-customer';
import { ErrorMessageBanner } from '../error-message-banner';
import { dealerCustomer } from 'services/dealer-customer/types';
import { useFeedback } from 'providers/feedback-provider';
import { AssociatedDealerTitle } from '../associated-dealer-title';
import {
  AssociatedDealerBody,
  AssociatedDealerFooter
} from '../associated-dealer-parts';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { CatLoadingIndicator } from 'blocks-nextjs/blocks-react/components/LoadingIndicator';

type SearchResultsFormProps = {
  searchResultsForm: FormType;
  isModal?: boolean;
  dealerStoreId?: string;
};

export const SearchResultsForm = ({
  searchResultsForm,
  dealerStoreId,
  isModal = false
}: SearchResultsFormProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const {
    searchResultsDrawer,
    selectCustomerDrawer,
    selectCustomerModal,
    searchResultsModal
  } = useAssociatedDealer();
  const { data: searchData } = useDealerSearch();
  const { startSearch, isFetched, isError, data, isLoading } =
    useDealerCustomer(dealerStoreId);
  const { updateFeedback, dismissFeedback } = useFeedback();

  const searchResults = useMemo(
    () => searchData?.dealerCustomer ?? [],
    [searchData]
  );

  const currentDCNValue = searchResultsForm.watch(CUSTOMER_NUMBER_RADIO_NAME);
  //this will change after apis calls are all setted 750833 with user teps1-cat-support
  //looks only for customer number 000114 to show error 750833 with user  teps1-cat-support
  const handleSubmit = async () => {
    searchResultsForm.handleSubmit((data: DealerSearchResultsFormData) => {
      startSearch({
        customerNumber: data['customer-number-radio'],
        storeId: ''
      });
    })();
  };

  const validateDCN = (data: dealerCustomer) => {
    const isInvalid =
      !data?.customerNumber ||
      !data?.dealerCustomerBillingAddress?.firstName ||
      !data?.dealerStoreLocations?.stores;
    return isInvalid;
  };

  useEffect(() => {
    if (isFetched && !isError) {
      fireEvent({
        formStatus: STATUS_SUCCESS,
        formName: SEARCH_RESULTS_FORM_ID
      });
      const invalidDCN = validateDCN(data);
      if (invalidDCN) {
        updateFeedback(ERRORS.INVALID_DCN);
      } else {
        dismissFeedback();
        isModal
          ? selectCustomerModal.modalControl.open()
          : selectCustomerDrawer.openDrawer();
      }
    } else if (isFetched && isError) {
      fireEvent({
        formStatus: STATUS_FAIL,
        formName: SEARCH_RESULTS_FORM_ID
      });
      updateFeedback(ERRORS.INVALID_DCN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, isError, data]);

  useEffect(() => {
    dismissFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDCNValue]);

  const resultsCount = `${searchResults?.length} ${t('RESULTS')}`;
  const [resultsDisplayed, setResultsDisplayed] = useState(
    DEFAULT_SEARCH_RESULTS_QUANTITY
  );
  const visibleSearchResults = useMemo(
    () => searchResults?.slice(0, resultsDisplayed),
    [searchResults, resultsDisplayed]
  );

  const handleViewMore = () =>
    setResultsDisplayed(
      (currentNum) => currentNum + DEFAULT_SEARCH_RESULTS_QUANTITY
    );
  const moreResultsAvailable = searchResults?.length > resultsDisplayed;
  const title = replaceTokensInString(
    t('GENERAL_BACK_TO', { 0: t('SEARCH_RESULTS_TEXT1') })
  );

  return (
    <>
      <DrawerBackHeader
        handleClose={() => {
          isModal
            ? searchResultsModal.modalControl.close()
            : searchResultsDrawer.closeDrawer();
          dismissFeedback();
        }}
      >
        <AssociatedDealerTitle>{title}</AssociatedDealerTitle>
      </DrawerBackHeader>
      <AssociatedDealerBody isModal={isModal}>
        <AssociatedDealerForm
          id={SEARCH_RESULTS_FORM_ID}
          formMethods={searchResultsForm}
          onSubmit={handleSubmit}
        >
          <ErrorMessageBanner />
          <div className="mt-3">
            <h4 className="cat-u-theme-typography-title-sm m-0">
              {t('TITLE_SEARCH_RESULTS')}
            </h4>
          </div>
          <div className="mt-3">
            <p className="cat-u-theme-typography-body-sm m-0">{resultsCount}</p>
          </div>
          <div className="mt-3">
            <SearchResults
              name={CUSTOMER_NUMBER_RADIO_NAME}
              searchResults={visibleSearchResults}
              disabled={false}
            />
          </div>
          <Conditional test={moreResultsAvailable}>
            <div className="my-3 w-100 text-center">
              <CatButton
                type="button"
                className="text-center"
                onClick={handleViewMore}
              >
                {t('VIEW_MORE_PRODUCTS')}
              </CatButton>
            </div>
          </Conditional>
        </AssociatedDealerForm>
      </AssociatedDealerBody>
      <AssociatedDealerFooter isModal={isModal}>
        <CatButton
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {t('SELECT_CUST_ACCT')}
          <Conditional test={isLoading}>
            <CatLoadingIndicator className="ml-2" small={true} />
          </Conditional>
        </CatButton>
      </AssociatedDealerFooter>
    </>
  );
};
